import { Box } from '@mui/material';
import { getIndexColor } from './getIndexColor';
import React, { useEffect, useState } from 'react';
import { format, startOfMonth } from 'date-fns';
import {
  BAR_SEGMENT_ANIMATION_DURATION,
  clampBubbleHeight,
  DEFAULT_SELECTED_ITEM,
  GRAPH_HEIGHT,
  SELECTED_COLOR,
  type BarSegment,
  type GradeTestData,
  type NewSelection,
  type SelectedItem,
} from './constants';
import { MonthLabels } from './MonthLabels';
import { GradeLabels } from './GradeLabels';
import { StackedBarGraph } from './StackedBarGraph';
import { LessonBubble } from './LessonBubble';
import { HighestScoringTestBubble } from './HighestScoringTestBubble';

type YearChartProps = {
  grades: GradeTestData[];
  lessons: Array<{ date: Date }>;
};

export const YearChart = ({ grades, lessons }: YearChartProps) => {
  const [selectedItem, _setSelectedItem] = useState<SelectedItem>(DEFAULT_SELECTED_ITEM);
  const setSelectedItem = (newSelection: NewSelection) => {
    if (
      selectedItem.type === newSelection.type &&
      selectedItem.data.value === newSelection.data.value
    ) {
      _setSelectedItem(DEFAULT_SELECTED_ITEM);
    } else {
      switch (newSelection.type) {
        case 'grade':
          _setSelectedItem({ type: 'grade', data: newSelection.data });
          break;
        case 'month':
          _setSelectedItem({ type: 'month', data: newSelection.data });
          break;
        default:
          throw new Error();
      }
    }
  };
  const resetSelection = () => _setSelectedItem(DEFAULT_SELECTED_ITEM);
  const [selectedButton, setSelectedButton] = useState<HTMLButtonElement | null>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectedButton && !selectedButton.contains(event.target as Node)) {
        resetSelection();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [resetSelection]);
  const lessonHeight = GRAPH_HEIGHT / lessons.length;
  const gradeBarSegments = getGradeBarSegments(grades, lessons, lessonHeight);
  const selectedGradeBarSegment = getSelectedGradeBarSegment(gradeBarSegments, selectedItem);
  const monthBarSegments = getMonthBarSegments(lessons, lessonHeight);

  return (
    <Box position="relative">
      <GradeLabels
        animationDelay={BAR_SEGMENT_ANIMATION_DURATION * monthBarSegments.length}
        gradeBarSegments={gradeBarSegments}
        grades={grades}
        selectedItem={selectedItem}
        setSelectedButton={setSelectedButton}
        setSelectedItem={setSelectedItem}
      />
      <MonthLabels
        monthBarSegments={monthBarSegments}
        selectedItem={selectedItem}
        setSelectedButton={setSelectedButton}
        setSelectedItem={setSelectedItem}
      />
      <StackedBarGraph
        monthBarSegments={monthBarSegments}
        selectedGradeBarSegment={selectedGradeBarSegment}
        selectedItem={selectedItem}
      />
      {selectedItem.type === 'grade' && (
        <HighestScoringTestBubble
          bottom={clampBubbleHeight(selectedItem.data.height)}
          date={selectedItem.data.date}
          score={selectedItem.data.testScore}
        />
      )}
      {selectedItem.type === 'month' && (
        <LessonBubble
          bottom={clampBubbleHeight(selectedItem.data.height)}
          numLessons={selectedItem.data.numLessons}
        />
      )}
    </Box>
  );
};

function getGradeBarSegments(
  grades: Array<{ name: string; highScoreDate: Date }>,
  lessons: Array<{ date: Date }>,
  lessonHeight: number
): BarSegment[] {
  return grades.reduce<BarSegment[]>((acc, grade) => {
    const gradeName = grade.name;
    if (!isFinite(lessonHeight) || lessons.length === 0) {
      return [
        ...acc,
        {
          barBottom: 0,
          barTop: GRAPH_HEIGHT,
          barHeight: GRAPH_HEIGHT,
          bgColor: SELECTED_COLOR,
          value: gradeName,
          numLessons: 0,
        },
      ];
    } else {
      const lessonsUpToTestDate = lessons.filter((lesson) => lesson.date < grade.highScoreDate);
      const numLessons = lessonsUpToTestDate.length;
      const barTop = numLessons * lessonHeight;
      const prevBar = acc.at(-1);
      const barBottom = prevBar?.barTop ?? 0;
      const barHeight = barTop - barBottom;
      const bgColor = SELECTED_COLOR;
      return [...acc, { barBottom, barTop, barHeight, bgColor, value: gradeName, numLessons }];
    }
  }, []);
}

function getSelectedGradeBarSegment(
  barSegments: BarSegment[],
  selectedItem: SelectedItem
): BarSegment | null {
  if (selectedItem.type !== 'grade') return null;
  return barSegments.find((segment) => segment.value === selectedItem.data.value) ?? null;
}

function getMonthBarSegments(lessons: Array<{ date: Date }>, lessonHeight: number): BarSegment[] {
  const uniqueMonthValues = Array.from(
    new Set(lessons.map((lesson) => startOfMonth(lesson.date).getTime()))
  );
  uniqueMonthValues.sort((a, b) => a - b);
  const months = uniqueMonthValues.map((monthValue) => new Date(monthValue));
  return months.reduce<BarSegment[]>((acc, month, index) => {
    const monthName = format(month, 'MMMM');
    const numLessons = lessons.filter(
      (lesson) => lesson.date.getMonth() === month.getMonth()
    ).length;
    const prevBar = acc.at(-1);
    const barBottom = prevBar?.barTop ?? 0;
    const barHeight = numLessons * lessonHeight;
    const barTop = barBottom + barHeight;
    const bgColor = getIndexColor(index);
    return [...acc, { barBottom, barTop, barHeight, bgColor, value: monthName, numLessons }];
  }, []);
}
